'use strict';

import React, { useMemo, useState, useCallback, useRef } from 'react'

import Button from '@material-ui/core/Button'

import PageWrapper from '@Layout/PageWrapper'
import Layout      from '@Layout/BackOffice'

import PaymentRow from '@/Lists/Rows/PaymentRow'
import DatePicker from '@/Form/V2/DatePicker'
import Dashboard  from '@/Lists/Dashboard'
import Select     from '@/Form/V2/Select'

import DepositDetailsModal from '@Modal/DepositDetailsModal'
import CheckDepositModal   from '@Modal/CheckDepositModal'

import SearchBar from '@Pure/SearchBar'
import Link      from '@Pure/Link'

import { payment_types } from '@Config/options'

import ModalsStore from '@Store/Modals'

const DASHBOARD = 'UserPaymentsList';

const DETAILS_MODAL = 'DepositDetails';

const payment_types_options =
[
	{ value: 'all', label: 'Tous' }
]
.concat(payment_types);

const types_options =
[
	{ value: 'all', label: 'Tout (factures & avoirs)' },
	{ value: 'EC', label: 'Facture CSE' },
	{ value: 'member', label: 'Facture membre ou élu' },
	{ value: 'partner', label: 'Facture partenaire' },
	{ value: 'credit_note', label: 'Avoir' }
];

const sortable_columns =
[
	'date'
];

const header =
[
	{
		label: 'Date', name: 'date'
	},
	{
		label: 'CSE ou membre', name: 'username'
	},
	{
		label: 'Numéro', name: 'text_number'
	},
	{
		label: 'Identifiant', name: 'number'
	},
	{
		label: 'Montant', name: 'amount'
	},
	{
		label: 'Mode de règlement', name: 'payment_type'
	},
	{
		label: 'En règlement de', name: 'bills'
	}
]
.map(opt => ({ ...opt, sortable: sortable_columns.includes(opt.name) }));

const fields = header.map(data => data.name);

const fields_with_timeout =
[
	'searched', 'number', 'text_number', 'deposit', 'amount', 'bill_number',
	'date_from', 'date_to'
];

function closeDepositDetailsModal ()
{
	ModalsStore.update(s => { s[DETAILS_MODAL] = { }; });
}

const storage_filters =
{
	searched: window.localStorage.getItem(DASHBOARD + '.searched') || '',
	number: window.localStorage.getItem(DASHBOARD + '.number') || '',
	text_number: window.localStorage.getItem(DASHBOARD + '.text_number') || '',
	order_number: window.localStorage.getItem(DASHBOARD + '.order_number') || '',
	bill_number: window.localStorage.getItem(DASHBOARD + '.bill_number') || '',
	deposit: window.localStorage.getItem(DASHBOARD + '.deposit') || '',
	amount: window.localStorage.getItem(DASHBOARD + '.amount') || '',
	date_from: window.localStorage.getItem(DASHBOARD + '.date_from') || '',
	date_to: window.localStorage.getItem(DASHBOARD + '.date_to') || ''
};

const NO_STATUS = { };

export default PageWrapper(function PaymentsDashboardPage (props)
{
	const search_timeout = useRef();

	const [deposit_modal, setDepositModal] = useState({ });

	const deposit_details_modal = ModalsStore.useState(s => s[DETAILS_MODAL]);

	const [instant_filters, setInstantFilters] = useState(
	{
		...storage_filters,

		payment_type: window.localStorage.getItem(DASHBOARD + '.payment_type') || '',
		type: window.localStorage.getItem(DASHBOARD + '.type') || ''
	});

	const [delayed_filters, setDelayedFilters] = useState({ ...storage_filters });

	const handleChange = useCallback(function ({ value, e, name })
	{
	// Prevent on mount trigger
		if (!name || e === null) return;

		window.localStorage.setItem(DASHBOARD + '.' + name, value);

		setInstantFilters(function (filters)
		{
			return { ...filters, [name]: value };
		});

		if (fields_with_timeout.includes(name) === false) return;

		if (search_timeout.current)
		{
			window.clearTimeout(search_timeout.current);
		}

		search_timeout.current = window.setTimeout(function ()
		{
			search_timeout.current = undefined;

			setDelayedFilters(function (delayed_filters)
			{
				return { ...delayed_filters, [name]: value };
			});
		},
		320);
	},
	[]);

	const handleSearchChange = useCallback(function (value, data={ }, e)
	{
		handleChange({ value: value, name: data?.name, e });
	},
	[handleChange]);

	const handleSelectChange = useCallback(function (e)
	{
		handleChange({ value: e.target.value, name: e.target.name, e });
	},
	[]);

	const filters = useMemo(function ()
	{
		const filters =
		{
			searched: delayed_filters.searched,
			number: delayed_filters.number,
			text_number: delayed_filters.text_number,
			payment_type: instant_filters.payment_type,
			order_number: instant_filters.order_number,
			bill_number: instant_filters.bill_number,
			type: instant_filters.type,
			deposit: delayed_filters.deposit,
			amount: delayed_filters.amount,
			date_from: delayed_filters.date_from,
			date_to: delayed_filters.date_to
		};

		return filters;
	},
	[instant_filters, delayed_filters]);

	const openDepositModal = useCallback(function (e)
	{
		setDepositModal({ open: true, trg: e?.target });
	},
	[]);

	const closeDepositModal = useCallback(e => { setDepositModal({ }); }, []);

	return (
		<Layout page_title="Gestion des règlements" history={ props.history }>

			<div className="page-content">

				<div className="o-grid-5 u-flex-wrap u-content-start
					u-margin-bottom-15 u-items-end">

					<div className="o-grid__item u-flex u-items-end u-gap-10">
						<DatePicker name="date_from" label="Du"
							onChange={ handleChange }
							value={ instant_filters.date_from }
							bemit={ ['50%'] }
						/>

						<DatePicker name="date_to" label="Au"
							onChange={ handleChange }
							value={ instant_filters.date_to }
							bemit={ ['50%'] }
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Recherche (CSE ou Membre)"
							handleChange={ handleSearchChange }
							value={ instant_filters.searched }
							name="searched"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="N° de règlement"
							handleChange={ handleSearchChange }
							value={ instant_filters.text_number }
							name="text_number"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="N° de commande"
							handleChange={ handleSearchChange }
							value={ instant_filters.order_number }
							name="order_number"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="N° de facture"
							handleChange={ handleSearchChange }
							value={ instant_filters.bill_number }
							name="bill_number"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Rechercher par identifiant"
							handleChange={ handleSearchChange }
							value={ instant_filters.number }
							name="number"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Rechercher par bordereau"
							handleChange={ handleSearchChange }
							value={ instant_filters.deposit }
							name="deposit"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Rechercher par Montant"
							handleChange={ handleSearchChange }
							value={ instant_filters.amount }
							name="amount"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<Select onChange={ handleSelectChange }
							label="Mode de règlement"
							value={ instant_filters.payment_type }
							name="payment_type"
							options={ payment_types_options }
						/>
					</div>

					<div className="o-grid__item">
						<Select onChange={ handleSelectChange }
							label="Règlement sur"
							value={ instant_filters.type }
							name="type"
							options={ types_options }
						/>
					</div>
				</div>

				<div className="o-content">

					<div className="u-flex u-content-between u-margin-bottom-20">
						<Button onClick={ openDepositModal }
							className="c-btn c-btn--green u-no-margin-top">
							Remise de chèques
						</Button>

						<Link href="/gestion-des-reglements/nouveau"
							className="c-btn c-btn--add u-no-margin-top">
							Nouveau règlement
						</Link>
					</div>

				</div>

				<div className="o-content">
					<Dashboard model="Payment" items-per-page={ 50 }
						url="/dashboard/payments" filters={ filters }
						header={ header } fields={ fields }
						order="desc" order-by="date"
						disable-sort={ filters.searched || filters.number ||
							filters.text_number || filters.order_number || filters.bill_number || filters.amount ? true : false }
						component={ PaymentRow }
						request-update="UpdateUserPaymentsList"
					/>
				</div>
			</div>

			<CheckDepositModal open={ deposit_modal.open === true }
				trg={ deposit_modal.trg }
				onClose={ closeDepositModal }
			/>

			<DepositDetailsModal open={ deposit_details_modal?.open === true }
				id={ deposit_details_modal?.id }
				trg={ deposit_details_modal?.trg }
				onClose={ closeDepositDetailsModal }
			/>
		</Layout>
	);
});